import React, { useEffect, useState, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import consumer from "../channels/consumer";
import CampaignReview from '../components/CampaignReview';
import InfoWithTooltip from '../components/meta-ads/InfoWithTooltip';
// Import ReportDisplay component for Reports tab
import ReportDisplay from '../components/meta-ads/ReportDisplay';

const CampaignDetailPage = () => {
  const { campaignId, companySlug } = useParams();
  const navigate = useNavigate();

  // Helper: append company_slug to API URLs for per-tab company scoping
  const scopedUrl = (url) => {
    if (!companySlug) return url;
    const separator = url.includes('?') ? '&' : '?';
    return `${url}${separator}company_slug=${companySlug}`;
  };

  // Helper: build slug-scoped Meta Ads AI path
  const metaAdsPath = companySlug ? `/marketing/meta_ads_ai/companies/${companySlug}` : '/marketing/meta_ads_ai';
  const [campaign, setCampaign] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');

  // Get company ID and admin status from DOM
  const companyId = useMemo(() => {
    const rootElement = document.getElementById('react-root');
    return rootElement?.getAttribute('company-id');
  }, []);
  const isAdmin = useMemo(() => {
    const rootElement = document.getElementById('react-root');
    return rootElement?.getAttribute('is-admin') === 'true';
  }, []);

  // Event ID derived from campaign (used by popstate handler and breadcrumbs below)
  const eventId = campaign?.meta_ads_ai_report?.event?.id ?? campaign?.event_id;

  // Intercept browser back button: navigate to MetaAdsAI page with correct event state
  // instead of falling through to the Turbolinks/Rails history entry
  useEffect(() => {
    const handlePopState = () => {
      navigate(metaAdsPath, { state: { selectedEventId: eventId, viewMode: 'campaigns' } });
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [navigate, metaAdsPath, eventId]);

  useEffect(() => {
    if (!campaignId) {
      navigate(metaAdsPath);
      return;
    }

    // Fetch campaign with fullResponse from database
    fetch(scopedUrl(`/api/facebook/campaigns/${campaignId}`), {
      headers: {
        'Accept': 'application/json'
      }
    })
      .then(res => {
        if (!res.ok) {
          throw new Error('Campaign not found');
        }
        return res.json();
      })
      .then(data => {
        console.log('Campaign data from backend:', data);
        setCampaign(data);
        setLoading(false);
      })
      .catch(error => {
        console.error('Error fetching campaign:', error);
        toast.error('Failed to load campaign');
        navigate(metaAdsPath);
      });
  }, [campaignId, navigate]);

  // ActionCable subscription for real-time updates
  useEffect(() => {
    if (!consumer) {
      return;
    }
    
    if (!companyId) {
      return;
    }
    
    const alertConfigurations = {
      position: "top-right",
      autoClose: 5000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      theme: "light",
    };
    
    const subscription = consumer.subscriptions.create(
      { 
        channel: "CompanyPostsChannel", 
        company_id: companyId
      },
      {
        initialized() {
        },
        connected() {
          try {
            this.perform('test_connection');
          } catch (err) {
            console.error("[ActionCable] Error performing test_connection:", err);
          }
        },
        disconnected() {
          console.warn("[ActionCable] Disconnected from CompanyPostsChannel");
        },
        rejected() {
          console.error("[ActionCable] Subscription rejected - check Rails logs (server-side)");
        },
        received(data) {
          try {
            // Helper function to refresh campaign data
            const refreshCampaign = () => {
              fetch(scopedUrl(`/api/facebook/campaigns/${campaignId}`), {
                headers: { 'Accept': 'application/json' }
              })
                .then(res => res.json())
                .then(updatedData => {
                  setCampaign(updatedData);
                })
                .catch(err => console.error('Error refreshing campaign:', err));
            };
            
            // Handle ads_data_created - refresh campaign data when ads are created
            if (data?.action === "ads_data_created") {
              refreshCampaign();
            }
            
            // Handle ads_created - refresh campaign when ads are created
            if (data?.action === "ads_created") {
              toast.success(data.message || 'Ads created successfully', alertConfigurations);
              refreshCampaign();
            }
            
            // Handle campaign_populated - refresh when campaign is populated from payload
            if (data?.action === "campaign_populated") {
              if (data.campaign_id && data.campaign_id.toString() === campaignId.toString()) {
                toast.success('Campaign updated successfully', alertConfigurations);
                refreshCampaign();
              }
            }
            
            // Handle ad publishing progress
            if (data?.action === "ad_publish_progress") {
              toast.info(data.message || 'Publishing ad...', alertConfigurations);
              // Refresh campaign to show updated status
              refreshCampaign();
            }
            
            // Handle ad publishing errors
            if (data?.action === "ad_publish_error") {
              toast.error(data.message || 'Failed to publish ad', alertConfigurations);
              refreshCampaign();
            }
            
            // Handle campaign population errors
            if (data?.action === "campaign_populate_error") {
              toast.error(data.message || 'Failed to populate campaign', alertConfigurations);
            }
            
            // Handle general errors
            if (data?.action === "error" || data?.error) {
              toast.error(data.message || data.error || 'An error occurred', alertConfigurations);
            }
          } catch (err) {
            console.error("[ActionCable] Error handling received data:", err);
          }
        }
      }
    );
    
    return () => {
      subscription?.unsubscribe();
    };
  }, [companyId, campaignId]);

  // Extract fullResponse from campaign
  const fullResponse = useMemo(() => {
    return campaign?.fullResponse || campaign?.ai_explanations?._full_response?.value || null;
  }, [campaign]);

  // Get report ID from campaign for Reports tab
  const reportId = useMemo(() => {
    return campaign?.meta_ads_ai_report?.id || null;
  }, [campaign]);

  // Event info for breadcrumb and "Back to Campaigns" (return to event's campaigns list)
  // eventId is derived above (before useEffect that depends on it)
  const eventName = campaign?.meta_ads_ai_report?.event?.name || campaign?.display_event_name || 'Event';
  const campaignName = campaign?.name || campaign?.campaign_name || 'Campaign';

  const backToCampaignsState = eventId != null ? { selectedEventId: eventId, viewMode: 'campaigns' } : undefined;

  const handleClose = () => {
    navigate(metaAdsPath, { state: backToCampaignsState });
  };

  const handleApproveCampaign = async (campaignId) => {
    // TODO: Implement approve logic
    console.log('Approve campaign:', campaignId);
    toast.success('Campaign approved');
    navigate(metaAdsPath, { state: backToCampaignsState });
  };

  const handleRejectCampaign = async (campaignId) => {
    // TODO: Implement reject logic
    console.log('Reject campaign:', campaignId);
    toast.info('Campaign rejected');
    navigate(metaAdsPath, { state: backToCampaignsState });
  };

  const handleArchiveCampaign = async (campaignId) => {
    // TODO: Implement archive logic
    console.log('Archive campaign:', campaignId);
    toast.info('Campaign archived');
    navigate(metaAdsPath, { state: backToCampaignsState });
  };

  // Helper function to extract ad data from fullResponse
  const extractAdDataFromFullResponse = (fullResponse, adId) => {
    if (!fullResponse || !fullResponse.ad_sets) return null;
    
    // Flatten all ads from all ad sets
    const allAds = [];
    fullResponse.ad_sets.forEach(adSet => {
      if (adSet.ads && Array.isArray(adSet.ads)) {
        adSet.ads.forEach(ad => {
          allAds.push({ ...ad, adSet });
        });
      }
    });
    
    // Find ad by matching with database ad ID or by index
    // For now, we'll match by order/index since we don't have a direct mapping
    return allAds;
  };

  // Helper function to get field value from fields array
  const getFieldValueFromFields = (fields, apiKey, defaultValue = null) => {
    if (!fields || !Array.isArray(fields)) return defaultValue;
    const field = fields.find(f => f.api_key === apiKey);
    return field?.value ?? defaultValue;
  };

  // Get explanation for strategy summary (e.g. budget)
  const getFieldExplanation = (fields, apiKeys, fallback = null) => {
    if (!fields || !Array.isArray(fields)) return fallback;
    const field = fields.find(f => apiKeys.includes(f.api_key));
    return field?.explanation || fallback;
  };

  const campaignFieldsForHeader = fullResponse?.campaign?.fields || [];
  const budgetExplanationHeader = getFieldExplanation(
    campaignFieldsForHeader,
    ['campaign.budget_amount_total', 'campaign.budget_amount_daily', 'campaign.budget_type'],
    'Budget sets your total or daily spend. Meta distributes it across ad sets to optimize for your objective.'
  );
  const goalExplanationHeader = getFieldExplanation(
    campaignFieldsForHeader,
    ['campaign.objective'],
    'The campaign objective tells Meta what result you want (e.g. conversions, traffic, awareness). It determines how your ads are optimized and delivered.'
  );
  const structureExplanationHeader = (() => {
    const adSetCount = (fullResponse?.ad_sets || []).length;
    const adCount = (fullResponse?.ad_sets || []).reduce((sum, s) => sum + (s?.ads?.length || 0), 0);
    return `This campaign has 1 campaign, ${adSetCount} ad set(s), and ${adCount} ad(s). Each ad set can target a different audience segment while sharing the campaign budget and objective.`;
  })();
  const targetingExplanationHeader = getFieldExplanation(
    (fullResponse?.ad_sets || []).flatMap(s => s?.fields || []),
    ['ad_set.role', 'ad_set.detailed_targeting.interests'],
    'Targeting defines who sees your ads. Broad prospecting reaches new audiences, while retargeting re-engages people who already interacted with your brand.'
  );

  const handleSaveCampaign = async (campaignData, action = "update") => {
    try {
      // Campaign-only update (used by Overview tab Save Changes)
      if (action === "update_campaign") {
        // Ad account is optional for draft campaigns from reports; only send if selected
        // Always PATCH the campaign (e.g. ad account when set) so the user gets feedback
        const updateResponse = await fetch(scopedUrl(`/api/facebook/campaigns/${campaignId}`), {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')
          },
          body: JSON.stringify({
            facebook_ad_account_id: campaignData.facebook_ad_account_id
          })
        });

        if (!updateResponse.ok) {
          const errorData = await updateResponse.json().catch(() => ({}));
          toast.error(errorData.error || 'Failed to update campaign ad account');
          return;
        }

        toast.success('Campaign updated successfully');

        // Always refresh campaign data (keeps UI in sync)
        const refreshResponse = await fetch(scopedUrl(`/api/facebook/campaigns/${campaignId}`), {
          headers: { 'Accept': 'application/json' }
        });
        if (refreshResponse.ok) {
          const refreshedData = await refreshResponse.json();
          setCampaign(refreshedData);
        }
        return;
      }

      // If action is "schedule", just schedule existing ads without saving
      if (action === "schedule") {
        // Fetch ads for this campaign using campaign_id parameter
        const adsResponse = await fetch(scopedUrl(`/facebook_ads?campaign_id=${campaignId}&include_details=true`), {
          headers: { 'Accept': 'application/json' }
        });
        
        if (adsResponse.ok) {
          const adsData = await adsResponse.json();
          // Flatten grouped data (grouped by date) into a single array
          const campaignAds = Object.values(adsData).flat();
          
          if (campaignAds.length === 0) {
            toast.error('No ads found for this campaign');
            return;
          }
          
          // Schedule and approve each ad
          const schedulePromises = campaignAds.map(ad => {
            if (ad.status !== 'published' && ad.status !== 'scheduled') {
              return fetch(scopedUrl(`/facebook_ads/${ad.id}/schedule_and_approve`), {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')
                },
                body: JSON.stringify({
                  id: ad.id,
                  status: 'scheduled'
                })
              }).catch(err => {
                console.error(`Error scheduling ad ${ad.id}:`, err);
                return null;
              });
            }
            return Promise.resolve();
          });
          
          await Promise.all(schedulePromises);
          toast.success('Campaign approved and scheduled successfully!');
          
          // Refresh campaign data without reloading the page
          const refreshResponse = await fetch(scopedUrl(`/api/facebook/campaigns/${campaignId}`), {
            headers: { 'Accept': 'application/json' }
          });
          if (refreshResponse.ok) {
            const refreshedData = await refreshResponse.json();
            setCampaign(refreshedData);
          }
        } else {
          toast.error('Failed to fetch ads for campaign');
        }
        return;
      }
      
      // If action is "update", update campaign and individual ads using the update endpoint
      if (action === "update") {
        // Check if ad account was changed
        if (campaignData?.facebook_ad_account_id && campaignData.facebook_ad_account_id !== campaign?.facebook_ad_account_id) {
          try {
            const updateResponse = await fetch(scopedUrl(`/api/facebook/campaigns/${campaignId}`), {
              method: 'PUT',
              headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json',
                'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')
              },
              body: JSON.stringify({
                facebook_ad_account_id: campaignData.facebook_ad_account_id
              })
            });
            
            if (!updateResponse.ok) {
              const errorData = await updateResponse.json();
              toast.error(errorData.error || 'Failed to update campaign ad account');
            } else {
              toast.success('Campaign ad account updated successfully');
              // Refresh campaign data after successful update
              const refreshResponse = await fetch(scopedUrl(`/api/facebook/campaigns/${campaignId}`), {
                headers: { 'Accept': 'application/json' }
              });
              if (refreshResponse.ok) {
                const refreshedData = await refreshResponse.json();
                setCampaign(refreshedData);
              }
            }
          } catch (error) {
            console.error('Error updating campaign ad account:', error);
            toast.error('Failed to update campaign ad account');
          }
        }
        
        const fullResponse = campaignData?.fullResponse || campaign?.fullResponse;
        
        if (!fullResponse) {
          toast.error('No campaign data found to update');
          return;
        }
        
        // Fetch ads for this campaign
        const adsResponse = await fetch(scopedUrl(`/facebook_ads?campaign_id=${campaignId}&include_details=true`), {
          headers: { 'Accept': 'application/json' }
        });
        
        if (!adsResponse.ok) {
          toast.error('Failed to fetch ads for campaign');
          return;
        }
        
        const adsData = await adsResponse.json();
        const campaignAds = Object.values(adsData).flat();
        
        if (campaignAds.length === 0) {
          toast.error('No ads found for this campaign');
          return;
        }
        
        // Extract all ads from fullResponse
        const fullResponseAds = extractAdDataFromFullResponse(fullResponse);
        
        // Get selected indexes from campaignData
        const selectedIndexes = campaignData?.selectedIndexes || {};
        
        // Update each ad
        const updatePromises = campaignAds.map((dbAd, index) => {
          // Find corresponding ad in fullResponse (match by index for now)
          const fullResponseAd = fullResponseAds && fullResponseAds[index] ? fullResponseAds[index] : null;
          
          if (!fullResponseAd) {
            console.warn(`No fullResponse data found for ad ${dbAd.id} at index ${index}`);
            return Promise.resolve();
          }
          
          const fields = fullResponseAd.fields || [];
          
          // Extract creative data with variations
          const headlines = getFieldValueFromFields(fields, 'ad.copy.headline_variations', []);
          const primaryTexts = getFieldValueFromFields(fields, 'ad.copy.primary_text_variations', []);
          const descriptions = getFieldValueFromFields(fields, 'ad.copy.description_variations', []);
          const adName = getFieldValueFromFields(fields, 'ad.name', dbAd.name);
          const cta = getFieldValueFromFields(fields, 'ad.call_to_action', 'get_tickets');
          const destinationUrl = getFieldValueFromFields(fields, 'ad.destination_url');
          const adNotes = getFieldValueFromFields(fields, 'ad.notes');
          
          // Extract ad set data
          const adSetFields = fullResponseAd.adSet?.fields || [];
          const adSetName = getFieldValueFromFields(adSetFields, 'ad_set.name');
          
          // Get selected indexes for this ad (index corresponds to ad position)
          const adSelectedIndexes = selectedIndexes[index] || { headline: 0, message: 0, description: 0 };
          
          // Prepare creative update data - ONLY selected indexes, NOT the variations arrays
          const creativeData = {
            name: adName,
            // Don't send message/headline/description arrays - they're already in the database
            // Only send the selected indexes
            selected_message_index: adSelectedIndexes.message || 0,
            selected_headline_index: adSelectedIndexes.headline || 0,
            selected_description_index: adSelectedIndexes.description || 0,
            link: destinationUrl || dbAd.creative?.link,
            cta_type: cta,
            notes: adNotes
          };
          
          // Prepare ad set update data
          const adSetData = {
            name: adSetName || dbAd.ad_set?.name
          };
          
          // Call update endpoint
          return fetch(scopedUrl(`/facebook_ads/${dbAd.id}`), {
            method: 'PATCH',
            headers: {
              'Content-Type': 'application/json',
              'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')
            },
            body: JSON.stringify({
              name: adName,
              creative: creativeData,
              ad_set: adSetData,
              campaign_id: campaignId
            })
          }).then(res => res.json()).catch(err => {
            console.error(`Error updating ad ${dbAd.id}:`, err);
            return { success: false, error: err.message };
          });
        });
        
        const results = await Promise.all(updatePromises);
        const successCount = results.filter(r => r.success !== false).length;
        
        if (successCount === campaignAds.length) {
          toast.success('Campaign updated successfully!');
          
          // Refresh campaign data without reloading the page
          const refreshResponse = await fetch(scopedUrl(`/api/facebook/campaigns/${campaignId}`), {
            headers: { 'Accept': 'application/json' }
          });
          if (refreshResponse.ok) {
            const refreshedData = await refreshResponse.json();
            setCampaign(refreshedData);
          }
        } else {
          toast.warning(`Updated ${successCount} of ${campaignAds.length} ads`);
          
          // Still refresh campaign data even if some failed
          const refreshResponse = await fetch(scopedUrl(`/api/facebook/campaigns/${campaignId}`), {
            headers: { 'Accept': 'application/json' }
          });
          if (refreshResponse.ok) {
            const refreshedData = await refreshResponse.json();
            setCampaign(refreshedData);
          }
        }
        return;
      }
      
      // Otherwise, save the campaign using populate_from_payload (for initial creation)
      const payload = campaignData?.fullResponse || transformCampaignToPayload(campaignData);
      const eventId = campaignData?.event_id || campaignData?.event_data?.event_id || null;

      const response = await fetch(scopedUrl('/api/facebook/campaigns/populate_from_payload'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')
        },
        body: JSON.stringify({
          payload: payload,
          event_id: eventId
        })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        toast.success('Campaign saved successfully!');
        
        // Refresh campaign data
        const refreshResponse = await fetch(scopedUrl(`/api/facebook/campaigns/${campaignId}`), {
          headers: { 'Accept': 'application/json' }
        });
        if (refreshResponse.ok) {
          const refreshedData = await refreshResponse.json();
          setCampaign(refreshedData);
        }
      } else {
        toast.error(data.error || data.errors?.join(', ') || 'Failed to save campaign');
      }
    } catch (error) {
      toast.error('Error saving campaign: ' + error.message);
      console.error('Save campaign error:', error);
    }
  };

  const transformCampaignToPayload = (campaign) => {
    if (campaign?.fullResponse) {
      return campaign.fullResponse;
    }
    // Fallback transformation logic (same as MetaAdsAIPage)
    return campaign?.fullResponse || {};
  };

  if (loading) {
    return (
      <div className="campaign-detail-page">
        <div className="campaign-detail-page__loading">
          <p>Loading campaign...</p>
        </div>
      </div>
    );
  }

  // Helper function to get field value (same as CampaignReview) - must be defined before use
  const getFieldValue = (fields, apiKey, defaultValue = null) => {
    if (!fields || !Array.isArray(fields)) return defaultValue;
    const field = fields.find(f => f.api_key === apiKey);
    return field?.value ?? defaultValue;
  };

  if (!campaign) {
    return (
      <div className="campaign-detail-page">
        <div className="campaign-detail-page__not-found">
          <p>Campaign not found</p>
        </div>
      </div>
    );
  }

  // Calculate campaign structure - safely handle missing data
  const adSets = fullResponse?.ad_sets || [];
  const totalAds = adSets.reduce((sum, adSet) => sum + (adSet?.ads?.length || 0), 0);
  const structure = `1/${adSets.length}/${totalAds}`; // campaign/ad sets/ads
  
  // Get targeting info
  const targetingTypes = [];
  adSets.forEach(adSet => {
    if (!adSet || !adSet.fields) return;
    const role = getFieldValue(adSet.fields, 'ad_set.role', 'prospecting');
    if (role === 'prospecting' && !targetingTypes.includes('Broad')) {
      targetingTypes.push('Broad');
    }
    if (role === 'retargeting' && !targetingTypes.includes('Retargeting')) {
      targetingTypes.push('Retargeting');
    }
    const interests = getFieldValue(adSet.fields, 'ad_set.detailed_targeting.interests');
    if (interests && !targetingTypes.includes('Interest-based')) {
      targetingTypes.push('Interest-based');
    }
  });

  // Get budget info
  const campaignFields = fullResponse?.campaign?.fields || [];
  const budgetTotal = getFieldValue(campaignFields, 'campaign.budget_amount_total');
  const budgetDaily = getFieldValue(campaignFields, 'campaign.budget_amount_daily');
  const objective = getFieldValue(campaignFields, 'campaign.objective') || campaign?.objective;
  
  // Convert budget from cents to dollars for display
  const budgetTotalDollars = budgetTotal ? budgetTotal / 100 : null;
  const budgetDailyDollars = budgetDaily ? budgetDaily / 100 : null;
  
  const budgetDisplay = budgetTotalDollars 
    ? `$${budgetTotalDollars.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} lifetime` 
    : budgetDailyDollars 
      ? `~$${budgetDailyDollars.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}/day`
      : 'Not set';

  // Editable when draft or failed; synced (read-only) when has Meta id or status is active/paused/etc.
  const isSynced = Boolean(
    campaign?.facebook_campaign_id ||
    (campaign?.status && !['draft', 'failed'].includes(String(campaign.status).toLowerCase()))
  );

  return (
    <div className="campaign-detail-page">
      <div className="campaign-detail-page__inner">
        {/* Page Header: breadcrumb (same across all tabs) */}
        <div className="campaign-detail-page__section">
          <div className="campaign-detail-page__header-row">
            <div>
              <nav className="campaign-detail-page__breadcrumb" aria-label="Breadcrumb">
                <Link to={metaAdsPath} className="campaign-detail-page__breadcrumb-link">
                  Events
                </Link>
                <span className="campaign-detail-page__breadcrumb-sep" aria-hidden="true"> &gt; </span>
                {eventId != null ? (
                  <>
                    <Link
                      to={metaAdsPath}
                      state={{ selectedEventId: eventId, viewMode: 'campaigns' }}
                      className="campaign-detail-page__breadcrumb-link"
                      title={eventName}
                    >
                      {eventName && eventName.length > 50 ? eventName.slice(0, 50) + '...' : eventName}
                    </Link>
                    <span className="campaign-detail-page__breadcrumb-sep" aria-hidden="true"> &gt; </span>
                  </>
                ) : (
                  <>
                    <span className="campaign-detail-page__breadcrumb-text" title={eventName}>{eventName && eventName.length > 50 ? eventName.slice(0, 50) + '...' : eventName}</span>
                    <span className="campaign-detail-page__breadcrumb-sep" aria-hidden="true"> &gt; </span>
                  </>
                )}
                <span className="campaign-detail-page__breadcrumb-text">Campaigns</span>
                <span className="campaign-detail-page__breadcrumb-sep" aria-hidden="true"> &gt; </span>
                <span className="campaign-detail-page__breadcrumb-text campaign-detail-page__breadcrumb-current" title={campaignName}>
                  {campaignName && campaignName.length > 50 ? campaignName.slice(0, 50) + '...' : campaignName}
                </span>
              </nav>
              {!isSynced && (
                <span className="campaign-detail-page__badge">
                  AI DRAFT REVIEW
                </span>
              )}
              <h1 className="campaign-detail-page__title">
                {campaignName}
              </h1>
              {eventName && eventName !== 'Event' && (
                <p className="campaign-detail-page__subtitle">
                  {eventName}
                </p>
              )}
            </div>
          </div>

          {/* Campaign Details Header (Strategy Summary) */}
          <div className="campaign-detail-page__strategy-card">
            <h3 className="campaign-detail-page__strategy-title">
              STRATEGY SUMMARY
            </h3>
            <div className="campaign-detail-page__strategy-grid">
              <div>
                <div className="campaign-detail-page__strategy-label-wrap">
                  <span className="campaign-detail-page__strategy-label">GOAL</span>
                  <InfoWithTooltip text={goalExplanationHeader} />
                </div>
                <div className="campaign-detail-page__strategy-value">{objective || 'Not set'}</div>
                <div className="campaign-detail-page__strategy-note">Tracked via Meta Pixel</div>
              </div>
              <div>
                <div className="campaign-detail-page__strategy-label-wrap">
                  <span className="campaign-detail-page__strategy-label">BUDGET</span>
                  <InfoWithTooltip text={budgetExplanationHeader} />
                </div>
                <div className="campaign-detail-page__strategy-value">{budgetDisplay}</div>
              </div>
              <div>
                <div className="campaign-detail-page__strategy-label-wrap">
                  <span className="campaign-detail-page__strategy-label">STRUCTURE</span>
                  <InfoWithTooltip text={structureExplanationHeader} />
                </div>
                <div className="campaign-detail-page__strategy-value">{structure}</div>
                <div className="campaign-detail-page__strategy-note">campaign / ad sets / ads</div>
              </div>
              <div>
                <div className="campaign-detail-page__strategy-label-wrap">
                  <span className="campaign-detail-page__strategy-label">TARGETING</span>
                  <InfoWithTooltip text={targetingExplanationHeader} />
                </div>
                <div className="campaign-detail-page__targeting-wrap">
                  {targetingTypes.length > 0 ? targetingTypes.map((type, idx) => (
                    <span key={idx} className="campaign-detail-page__targeting-pill">
                      {type}
                    </span>
                  )) : (
                    <span className="campaign-detail-page__targeting-empty">Not set</span>
                  )}
                </div>
              </div>
            </div>
          </div>

          {isSynced && (
            <p className="campaign-detail-page__synced-notice" style={{ margin: '0 0 1rem', padding: '0.75rem 1rem', background: '#f0f4f8', borderRadius: 8, fontSize: '0.875rem', color: '#374151' }}>
              This campaign has been synced to Meta Ads Manager and is currently paused. Edit in Meta going forward.
              {campaign?.meta_ads_manager_url && (
                <> {' '}
                  <a href={campaign.meta_ads_manager_url} target="_blank" rel="noopener noreferrer" style={{ fontWeight: 600 }}>
                    Open in Meta Ads Manager →
                  </a>
                </>
              )}
            </p>
          )}

          {/* Tab Navigation */}
          <div className="campaign-detail-page__tabs">
            {[
              { id: 'overview', label: 'Overview' },
              { id: 'targeting', label: 'Targeting' },
              { id: 'creative', label: 'Creative' },
              { id: 'reports', label: 'Marketing Plans' }
            ].map((tab) => (
              <button
                key={tab.id}
                type="button"
                className={`campaign-detail-page__tab ${activeTab === tab.id ? 'campaign-detail-page__tab--active' : ''}`}
                onClick={() => setActiveTab(tab.id)}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Render content based on active tab */}
        <div className="campaign-detail-page__content-card">
          {activeTab === 'reports' ? (
            reportId ? (
              <ReportDisplay reportId={reportId} onClose={null} isAdmin={isAdmin} />
            ) : (
              <div className="campaign-detail-page__reports-empty">
                <p>No report data available for this campaign.</p>
                <p>This campaign was not generated from an AI report.</p>
              </div>
            )
          ) : (
            /* Other tabs - Use CampaignReview component */
            <CampaignReview
              campaign={campaign}
              isOpen={true}
              onClose={handleClose}
              onApprove={handleApproveCampaign}
              onReject={handleRejectCampaign}
              onArchive={handleArchiveCampaign}
              onSave={handleSaveCampaign}
              renderAsPage={true}
              activeTab={activeTab}
              onTabChange={setActiveTab}
              toast={toast}
              onRefetchCampaign={async () => {
                if (!campaignId) return;
                try {
                  const res = await fetch(scopedUrl(`/api/facebook/campaigns/${campaignId}`), { headers: { Accept: 'application/json' } });
                  const data = await res.json();
                  if (res.ok && data) setCampaign(data);
                } catch (e) {
                  console.error('Failed to refetch campaign:', e);
                }
              }}
              companySlug={companySlug}
            />
          )}
        </div>
      </div>
    </div>
  );
};


export default CampaignDetailPage;
